import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import {
  SwapVert as MovimientoIcon,
  ArrowUpward as IngresoIcon,
  ArrowDownward as EgresoIcon,
} from '@mui/icons-material';
import { usePosStore } from '../store/posStore';
import { PosCajaService } from '../services/posCajaService';

export const CashMovementDialog: React.FC = () => {
  const { cashMovementDialogOpen, setCashMovementDialogOpen } = usePosStore();
  const [tipo, setTipo] = useState<string>('INGRESO');
  const [concepto, setConcepto] = useState('');
  const [monto, setMonto] = useState<number>(0);
  const [descripcion, setDescripcion] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!concepto.trim()) {
      setError('Ingrese el concepto');
      return;
    }
    if (monto <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await PosCajaService.registrarMovimiento({
        tipo,
        concepto,
        monto,
        descripcion: descripcion || undefined,
      });
      setTipo('INGRESO');
      setConcepto('');
      setMonto(0);
      setDescripcion('');
      setCashMovementDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al registrar movimiento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={cashMovementDialogOpen}
      onClose={() => setCashMovementDialogOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MovimientoIcon color="primary" />
          Movimiento de Caja
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <ToggleButtonGroup
            value={tipo}
            exclusive
            onChange={(_e, val) => val && setTipo(val)}
            fullWidth
            size="small"
          >
            <ToggleButton value="INGRESO" color="success">
              <IngresoIcon sx={{ mr: 0.5 }} />
              Ingreso
            </ToggleButton>
            <ToggleButton value="EGRESO" color="error">
              <EgresoIcon sx={{ mr: 0.5 }} />
              Egreso
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Concepto"
            size="small"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            required
          />

          <TextField
            label="Monto"
            type="number"
            size="small"
            value={monto || ''}
            onChange={(e) => setMonto(Number(e.target.value))}
            slotProps={{ htmlInput: { min: 0, step: 0.1 } }}
            required
          />

          <TextField
            label="Descripcion (opcional)"
            size="small"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setCashMovementDialogOpen(false)} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          color={tipo === 'INGRESO' ? 'success' : 'error'}
        >
          {saving ? 'Guardando...' : `Registrar ${tipo === 'INGRESO' ? 'Ingreso' : 'Egreso'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashMovementDialog;
